import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import { SEASONS_PROP_TYPES } from '../../lib/prop-types/season';
import {
  FIXTURE_SUMMARY_STATISTICS,
  FIXTURE_STATISTICS_BREAKDOWN,
  KEY_PLAYER_STATISTICS,
  PLAYER_STATISTICS_TABLES,
} from '../../lib/prop-types/statistics';
import ClubRouter from '../../lib/routing';
import withTeam from '../../lib/with-team';
import NoBackground from '../../components/club-frame/header/no-background';
import SeoHead from '../../components/seo';
import TeamStatistics from '../../components/team/stats';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import { fetchStatisticsPageData } from '../../store/teams/stats/actions';
import { getCurrentClubSeasons, getActiveSeasonId } from '../../store/clubs/selectors';
import {
  isLoading,
  hasLoaded,
  getLoadingError,
  getFixtureStatistics,
  getPointStatistics,
  getPlayerStatistics,
  getPlayerStatisticsTables,
} from '../../store/teams/stats/selectors';

class TeamStatisticsPage extends React.Component {
  static async getInitialProps({ reduxStore, club, isServer, team, req, res, query }) {
    const initialProps = {
      activeSubSection: 'statistics',
      pageType: 'team.statistics',
      seasonId: query.season
        ? parseInt(query.season, 10)
        : getActiveSeasonId(reduxStore.getState()),
    };

    if (!isServer) {
      return initialProps;
    }

    const { error, accessLevel } = await reduxStore.dispatch(
      fetchStatisticsPageData(club.id, team.id, initialProps.seasonId, req),
    );

    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
    }

    return initialProps;
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { teamId: prevTeamId, seasonId: prevSeasonId } = prevProps;
    const { teamId, seasonId } = this.props;

    if (teamId !== prevTeamId || (teamId === prevTeamId && seasonId !== prevSeasonId)) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      loading,
      previouslyLoaded,
      loadPageData,
      club: { id: clubId },
      team: { id: teamId },
      seasonId,
    } = this.props;
    if (!loading && !previouslyLoaded) {
      loadPageData(clubId, teamId, seasonId);
    }
  };

  onSeasonFilterChangeHandler = (event) => {
    const seasonId = event.target.value;
    const {
      club,
      team: { id: teamId },
    } = this.props;

    ClubRouter.pushClubRoute(club, 'team.statistics', { teamId, season: seasonId });
  };

  render() {
    const {
      club,
      team,
      loading,
      loadingError,
      seasons,
      seasonId,
      fixtureStatistics,
      pointStatistics,
      playerStatistics,
      playerStatisticsTables,
      previouslyLoaded,
    } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    return (
      <>
        <SeoHead
          club={club}
          descriptionTranslationKey="teams:statistics_meta_description"
          route="team.statistics"
          routeParams={{ teamId: team.id }}
          translationValues={{ teamName: team.name }}
          titleTranslationKey="teams:statistics_page_title"
        />
        <NoBackground />
        <TeamStatistics
          club={club}
          isLoading={loading || !previouslyLoaded}
          clubPrimaryColour={club.colours.primary}
          sport={club.sport}
          teamId={team.id}
          seasons={seasons}
          seasonId={seasonId}
          onSeasonFilterChange={this.onSeasonFilterChangeHandler}
          fixtureStatistics={fixtureStatistics}
          pointStatistics={pointStatistics}
          playerStatistics={playerStatistics}
          playerStatisticsTables={playerStatisticsTables}
        />
      </>
    );
  }
}

TeamStatisticsPage.defaultProps = {
  loadingError: null,
  teamId: 0,
  seasonId: null,
  fixtureStatistics: null,
  pointStatistics: null,
  playerStatistics: null,
  playerStatisticsTables: null,
};

TeamStatisticsPage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
  teamId: PropTypes.number,
  seasonId: PropTypes.number,
  loadPageData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  seasons: SEASONS_PROP_TYPES.isRequired,
  fixtureStatistics: FIXTURE_SUMMARY_STATISTICS,
  pointStatistics: FIXTURE_STATISTICS_BREAKDOWN,
  playerStatistics: KEY_PLAYER_STATISTICS,
  playerStatisticsTables: PLAYER_STATISTICS_TABLES,
};

const dispatchToProps = {
  loadPageData: fetchStatisticsPageData,
};

const mapStateToProps = (state, { teamId, seasonId }) => ({
  loading: isLoading(state, teamId, seasonId),
  previouslyLoaded: hasLoaded(state, teamId, seasonId),
  loadingError: getLoadingError(state, teamId),
  seasons: getCurrentClubSeasons(state),
  fixtureStatistics: getFixtureStatistics(state, teamId, seasonId),
  pointStatistics: getPointStatistics(state, teamId, seasonId),
  playerStatistics: getPlayerStatistics(state, teamId, seasonId),
  playerStatisticsTables: getPlayerStatisticsTables(state, teamId, seasonId),
});

export default withTeam(connect(mapStateToProps, dispatchToProps)(TeamStatisticsPage));
